import type { Translation } from '../i18n-types.js';

const de: Translation = {
    common: {
        cancel: 'Abbrechen',
        save: 'Speichern',
        delete: 'Löschen',
        add: 'Hinzufügen',
        create: 'Erstellen',
        update: 'Aktualisieren',
        back: 'Zurück',
        loading: 'Lädt ...',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        defaultLanguage: 'Standardsprache',
        language: 'Sprache',
        languageEnglish: 'English (Englisch)',
        languageGerman: 'Deutsch',
        days: '{count} Tag{{e}}',
        refresh: 'Aktualisieren',
        close: 'Schließen',
    },
    appBar: {
        plan: 'Plan',
        adminPanel: 'Admin-Panel',
        history: 'Historie',
        event: 'Termin',
        logout: 'Abmelden',
        switchToLight: 'Helles Design aktivieren',
        switchToDark: 'Dunkles Design aktivieren',
    },
    menu: {
        profile: 'Profil',
        mailbox: 'Postfach',
        filterCategories: 'Kategorien filtern',
        showHistory: 'Historie anzeigen',
        hideHistory: 'Historie verbergen',
        addEvent: 'Termin hinzufügen',
        adminPanel: 'Admin-Panel',
        forecast: 'Anzahl Termine',
        manageCategories: 'Kategorien verwalten',
        manageMyCategories: 'Meine Kategorien',
        reviewCategoryRequests: 'Kategorienanfragen',
        scheduleTemplates: 'Zeitplan Vorlagen',
        yearSummary: 'Jahresansicht',
        personalForecast: 'Mein Zeitplan',
    },
    scheduleTemplateSets: {
        headerSubtitleEdit: 'Vorlagengruppen erstellen und bearbeiten',
        headerSubtitleView: 'Vorlagengruppen ansehen',
        newSetTab: 'Neue Gruppe',
        addTemplateLabel: 'Vorlage hinzufügen',
        addTemplatePlaceholder: 'Vorlagen zum Hinzufügen suchen',
        templatesInSetTitle: 'Vorlagen in Gruppe',
        noTemplatesAdded: 'Der Gruppe wurde noch keine Vorlagen hinzugefügt',
        newSetDialogTitle: 'Neue Vorlagen Gruppe erstellen',
        setNameLabel: 'Gruppen-Name',
        pleaseEnterName: 'Bitte gib einen Namen für die Gruppe ein',
        rename: 'Umbenennen',
        renameDialogTitle: 'Vorlagen Gruppe umbenennen',
        newNameLabel: 'Neuer Name',
        deleteConfirmTitle: 'Vorlagen Gruppe löschen',
        deleteConfirmMessage: 'Möchtest du diese Vorlagen Gruppe wirklich löschen?',
        failedToSave: 'Vorlagen Gruppe konnte nicht gespeichert werden',
        failedToDelete: 'Vorlagen Gruppe konnte nicht gelöscht werden',
        failedToRename: 'Vorlagen Gruppe konnte nicht umbenannt werden',
        nameExists: 'Ein Vorlagen Gruppe mit diesem Namen existiert bereits',
        remove: 'Entfernen',
        dragToReorder: 'Ziehen, um Reihenfolge zu ändern',
    },
    mainPage: {
        title: 'Plan',
        loadingAdminData: 'Admin-Daten werden geladen ...',
    },
    event: {
        event: 'Veranstaltung',
        today: 'HEUTE',
        copyLink: 'Link kopieren',
        showAgenda: 'Agenda anzeigen',
        showFiles: 'Dateien anzeigen',
        edit: 'Termin bearbeiten',
        absent: 'Abwesend',
        present: 'Anwesend',
        absenceWarning:
            'Du kannst dich nicht als abwesend markieren, während du einer Kategorie zugeordnet bist. Bitte fordere eine Änderung an oder kontaktiere einen Administrator.',
        schedule: 'Zeitplan',
        unknownLocation: 'Unbekannter Veranstaltungsort',
        activateFilter: 'Kategorienfilter wieder aktivieren',
        deactivateFilter: 'Kategorienfilter deaktivieren',
    },
    changeRequests: {
        title: 'Tauschanfragen',
        noOpen: 'Es sind keine offenen Tauschanfragen vorhanden.',
    },
    eventModal: {
        createTitle: 'Termin erstellen',
        createSubtitle: 'Neuen Termin zum Kalender hinzufügen',
        editTitle: 'Termin bearbeiten',
        editSubtitle: 'Termindetails aktualisieren',
        eventDate: 'Termindatum',
        eventName: 'Terminname',
        startTime: 'Startzeit',
        endTime: 'Endzeit',
        location: 'Ort',
        eventNamePlaceholder: 'Terminname eingeben (z.B. Gottesdienst, Besondere Veranstaltung)',
        selectDatePrompt: 'Bitte wähle ein Datum',
        dateCannotBeInPast:
            'Termine können nicht in der Vergangenheit erstellt werden. Bitte wähle heute oder ein zukünftiges Datum.',
        excludedCategories: 'Ausgeschlossene Kategorien',
        resetToTemplate: 'Auf Vorlage zurücksetzen',
        clearAll: 'Alle ausgeschlossenen Kategorien löschen',
        dateChangeAdminOnlyHelper:
            'Nur Administratoren können das Datum eines bestehenden Termins ändern.',
        changeDateConfirmTitle: 'Termindatum ändern',
        changeDateConfirmMessage:
            'Du bist dabei, das Termindatum von {from} auf {to} zu ändern. Dadurch wird der gesamte Termin verschoben (und ggf. verbundene Daten beeinflusst). Bist du sicher?',
        changeDateConfirmYes: 'Ja, Datum ändern',
        enableDateEditing: 'Datum bearbeiten',
        canceled: 'Abgesagt',
    },
    filterModal: {
        title: 'Kategorien filtern',
        subtitle: '{visible} von {total} sichtbar',
        subtitleEmpty: 'Kategorien zum Anzeigen auswählen',
        applyFilters: 'Filter anwenden',
        showAll: 'Alle anzeigen',
        hideAll: 'Alle ausblenden',
        assignedCategoryTooltip: 'Du bist {name} zugewiesen - immer sichtbar',
        inactiveCategoryTooltip: '{name} ist inaktiv und kann nicht angezeigt werden',
        emptyCategoriesHint:
            'Keine Kategorien sichtbar. Du kannst weitere Kategorien anzeigen, indem du oben auf das Filtersymbol klickst und zusätzliche Kategorien auswählst.',
        emptyCategoriesHintShort:
            'Keine Kategorien zum Anzeigen. Bitte passe dein Kategorie Filter an, um mehr anzuzeigen.',
    },
    adminPage: {
        title: 'Admin-Panel',
        subtitle: 'Benutzer, Kategorien und Berechtigungen verwalten',
        backToPlan: 'Zurück zum Plan',
        addUser: 'Benutzer hinzufügen',
        categories: 'Kategorien',
        deleteConfirm: 'Bist du sicher? Dies kann nicht rückgängig gemacht werden',
    },
    adminScheduleTemplatesPage: {
        title: 'Zeitplan-Vorlagen',
        addTemplate: 'Vorlage hinzufügen',
        editTemplate: 'Vorlage bearbeiten',
        templateName: 'Vorlagenname',
        templateDuration: 'Vorlagendauer (in Minuten)',
        templateNameRequired: 'Vorlagenname darf nicht leer sein',
        deleteConfirm: 'Bist du sicher, dass du diese Vorlage löschen möchtest?',
        noTemplates: 'Keine Vorlagen gefunden',
        saveFailed: 'Vorlage konnte nicht gespeichert werden: {message}',
        deleteFailed: 'Vorlage konnte nicht gelöscht werden: {message}',
        actions: 'Aktionen',
        usedBySets: 'Verwendet in Gruppen',
    },
    adminEventsPage: {
        title: 'Terminverwaltung',
        subtitle: 'Verwalte Termine und wiederkehrende Ereignisse',
        events: 'Termine',
        addEvent: 'Termin hinzufügen',
        editEvent: 'Termin bearbeiten',
        eventName: 'Terminname',
        location: 'Ort',
        startTime: 'Startzeit',
        endTime: 'Endzeit',
        recurrenceType: 'Wiederholungstyp',
        recurrence: 'Wiederholung',
        status: 'Status',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        name: 'Name',
        time: 'Zeit',
        weekly: 'Wöchentlich',
        monthly: 'Monatlich',
        yearly: 'Jährlich',
        selectDaysOfWeek: 'Wochentage auswählen',
        monthlyPatterns: 'Monatliche Muster',
        yearlyPatterns: 'Jährliche Muster (Datum im Jahr)',
        week: 'Woche',
        day: 'Tag',
        month: 'Monat',
        addPattern: 'Muster hinzufügen',
        addDate: 'Datum hinzufügen',
        weeklyPrefix: 'Wöchentlich',
        monthlyPrefix: 'Monatlich',
        yearlyPrefix: 'Jährlich',
        excludedCategories: 'Ausgeschlossene Kategorien',
        noExcludedCategories: 'Keine Kategorien ausgeschlossen',
    },
    weekdays: {
        monday: 'Montag',
        tuesday: 'Dienstag',
        wednesday: 'Mittwoch',
        thursday: 'Donnerstag',
        friday: 'Freitag',
        saturday: 'Samstag',
        sunday: 'Sonntag',
    },
    weekPositions: {
        first: 'Erste',
        second: 'Zweite',
        third: 'Dritte',
        fourth: 'Vierte',
        last: 'Letzte',
    },
    months: {
        january: 'Januar',
        february: 'Februar',
        march: 'März',
        april: 'April',
        may: 'Mai',
        june: 'Juni',
        july: 'Juli',
        august: 'August',
        september: 'September',
        october: 'Oktober',
        november: 'November',
        december: 'Dezember',
    },
    admin: {
        backToAdmin: 'Zurück zum Admin-Panel',
    },
    adminCategoriesPage: {
        title: 'Kategorienverwaltung',
        subtitle: 'Kategorien und Einstellungen verwalten',
        addCategory: 'Kategorie hinzufügen',
        searchPlaceholder: 'Kategorien durchsuchen ...',
        editCategory: 'Kategorie bearbeiten',
        deleteConfirm:
            'Möchtest du diese Kategorie wirklich löschen? Dies kann nicht rückgängig gemacht werden.',
        activateConfirm: 'Möchtest du die Kategorie "{name}" wirklich {action}?',
        activateAction: 'aktivieren',
        deactivateAction: 'deaktivieren',
        activateTooltip: 'Kategorie aktivieren',
        deactivateTooltip: 'Kategorie deaktivieren',
        emptyStateSearch: 'Versuche, deine Suchkriterien anzupassen',
        emptyStateNoCategories: 'Erstelle deine erste Kategorie',
        allowMultipleAssignments: 'Mehrfache Zuweisung',
        minAssignments: 'Mindestanzahl',
        maxAssignments: 'Maximalanzahl',
        columns: {
            status: 'Status',
            assignments: 'Zuweisungen',
            setupTime: 'Aufbau (Min)',
            teardownTime: 'Abbau (Min)',
            actions: 'Aktionen',
        },
        notification: 'Benachrichtigung',
        notifyUnfilledLabel: 'Erinnerung senden, wenn nicht zugewiesen',
        notifyDaysBeforeLabel: '{days} Tag{{e}} vorher',
    },
    categoryForm: {
        categoryName: 'Kategoriename',
        icon: 'Symbol',
        iconHelperText: 'Symbol-Name',
        iconPlaceholder: 'z.B. music, microphone, book',
        group: 'Gruppe',
        groupPlaceholder: 'z.B. Anbetung, Technik, Verwaltung',
        setupTime: 'Aufbauzeit (Minuten)',
        setupTimeHelperText: 'Zeit vor Beginn der Veranstaltung',
        teardownTime: 'Abbauzeit (Minuten)',
        teardownTimeHelperText: 'Zeit nach Ende der Veranstaltung',
        sortPriority: 'Sortierpriorität',
        sortPriorityHelperText: 'Niedrigere Werte werden zuerst angezeigt (0-999)',
    },
    userCard: {
        roles: 'Rollen',
        admin: 'Administrator',
        scheduleEditor: 'Zeitplan Bearbeiter',
        scheduleTemplateEditor: 'Zeitplan Vorlagen Bearbeiter',
        eventEditor: 'Termin Bearbeiter',
        debug: 'Debug-Modus',
        debugTooltip:
            'Debug-Modus — zeigt eine schwebende Schaltfläche zum Aufnehmen und Hochladen von Screenshots',
        showPin: 'PIN anzeigen',
        hidePin: 'PIN verbergen',
        emailPlaceholder: 'benutzer@beispiel.de',
        pinPlaceholder: 'PIN',
        lastLogin: 'Letzter Login',
        never: 'Nie',
        categories: 'Kategorien & Verantwortlichkeiten',
        member: 'Mitglied',
        assistant: 'Aushilfe',
        responsible: 'Verantwortlich',
        proxies: 'Stellvertreter',
        noProxies: 'Keine Stellvertreter konfiguriert',
        addProxy: 'Stellvertreter hinzufügen',
        activeUser: 'Aktiver Benutzer',
        activeUserDescription: 'Benutzer kann sich anmelden und auf das System zugreifen',
        administrator: 'Administrator',
        administratorDescription: 'Benutzer hat vollständige Admin-Rechte',
        adminManagedByOidc: 'Admin-Status wird über OIDC-Gruppenmitgliedschaft verwaltet',
        showDetails: 'Details anzeigen',
        hideDetails: 'Details verbergen',
        deleteUser: 'Benutzer löschen',
        categoryHelperText:
            'Klicke auf den Dropdown-Pfeil bei jeder Kategorie, um die Rolle des Benutzers festzulegen: Mitglied, Helfer oder Verantwortlich.',
        removeMembership: 'Mitgliedschaft entfernen',
        unknownUser: 'Benutzer #{id}',
        themeDark: 'Dunkles Design',
        themeLight: 'Helles Design',
    },
    addUserModal: {
        title: 'Neuen Benutzer hinzufügen',
        subtitle: 'Neues Benutzerkonto erstellen',
        name: 'Name',
        email: 'E-Mail',
        authPin: 'Auth-PIN (4 Ziffern)',
        authPinHelperText: 'Gib eine 4-stellige PIN zur Authentifizierung ein',
        createUser: 'Benutzer erstellen',
    },
    addProxyModal: {
        title: 'Stellvertreter hinzufügen',
        subtitle: 'Stellvertreter zuweisen',
        selectProxy: 'Stellvertreter auswählen',
        selectProxyHelperText: 'Wähle einen Benutzer als Stellvertreter',
    },
    emptyState: {
        noUsers: 'Keine Benutzer gefunden',
        noActiveUsers: 'Keine aktiven Benutzer gefunden',
        noInactiveUsers: 'Keine inaktiven Benutzer gefunden',
        noAdmins: 'Keine Administratoren gefunden',
        addFirstUser: 'Füge deinen ersten Benutzer hinzu',
    },
    filterBar: {
        all: 'Alle',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        admin: 'Admin',
        searchPlaceholder: 'Suchen ...',
    },
    statsCards: {
        totalUsers: 'Benutzer gesamt',
        activeUsers: 'Aktive Benutzer',
        admins: 'Administratoren',
        categories: 'Kategorien',
    },
    categoryItem: {
        absent: 'Abwesend',
        cancelRequest: 'Anfrage abbrechen',
        notify: 'Benachrichtigen',
        requestReplacement: 'Ersatz anfragen',
        addToCalendar: 'Zum Kalender hinzufügen',
        addToFilter: 'Kategorie zum Filter hinzufügen',
        substitute: 'Als Ersatz einspringen',
        absentWarning: 'Du bist als abwesend markiert und kannst keine Kategorien bearbeiten',
        noResourcesAvailable: 'Keine Mitarbeiter zugewiesen',
        history: 'Historie',
        systemAssistantSaveDisabled:
            'Systemaushilfen können nicht als Zuweisungen gespeichert werden',
        deleteWindowExpiredTitle: 'Löschen nicht mehr möglich',
        deleteWindowExpiredMessage:
            'Du kannst dich nur innerhalb von 5 Minuten nach dem Eintragen selbst wieder aus einer Zuweisung entfernen. Damit wird verhindert, dass unbemerkte Änderungen entstehen. Wenn du einen Ersatz benötigst, kannst du eine Tauschanfrage stellen – Andere Nutzer können deinen Dienst übernehmen.',
    },

    audit: {
        title: 'Zuordnungshistorie',
        empty: 'Keine Einträge',
        by: 'von',
    },

    absentModal: {
        title: 'Abwesende Benutzer',
        user: 'Benutzer',
    },
    agendaModal: {
        title: 'Agenda',
        time: 'Zeit',
        topic: 'Thema',
        responsible: 'Verantwortlich',
    },
    filesModal: {
        title: 'Dateien',
        subtitle: 'Anhänge und Dokumente',
        noFiles: 'Keine Dateien verfügbar',
    },
    scheduleModal: {
        title: 'Zeitplan',
        addTopic: 'Thema hinzufügen',
        reorder: 'Neu anordnen',
        doneReordering: 'Anordnung abgeschlossen',
        noStartTimeWarning:
            'Für die Veranstaltung ist keine Startzeit festgelegt und es ist keine Standardzeit konfiguriert. Zeiten können nicht berechnet werden.',
        titleLabel: 'Titel',
        titleHelperText: 'Vorlage auswählen oder frei eingeben',
        titleHelperTextWithTags:
            'Vorlage auswählen oder frei eingeben. Wenn Tags gesetzt sind, bekommst du auch Vorschläge aus früheren Einträgen mit passenden Tags.',
        duration: 'Dauer (Minuten)',
        durationHelperText: 'Dauer in Minuten für diesen Eintrag',
        responsiblePerson: 'Verantwortliche Person',
        responsibleHelperText: 'Frei eingeben, aus der Liste wählen, oder {placeholder} verwenden.',
        notes: 'Notizen',
        responsibleLabel: 'Verantwortlich',
        minutesShort: 'Min',
        noScheduleItems: 'Noch keine Elemente',
        addFirstItem: 'Füge dein erstes Element hinzu',
        editScheduleItem: 'Element bearbeiten',
        addScheduleItem: 'Element hinzufügen',
        download: 'Herunterladen',
        downloadPdf: 'Als PDF herunterladen',
        showHighlights: 'Markierungen anzeigen',
        showNotes: 'Notizen anzeigen',
        showDuration: 'Dauer anzeigen',
        biggerFooter: 'Größere Fußzeile',
        pdfTitle: 'Veranstaltungszeitplan',
        pdfLocation: 'Veranstaltungsort',
        pdfTopic: 'Thema',
        pdfDuration: 'Dauer',
        pdfResponsible: 'Verantwortlich',
        pdfNote: 'Notiz',
        pdfTime: 'Zeit',
        pdfMinutes: 'min',
        lastEdited: 'Letzte Änderung',
        categoryFormat: '{category}: {responsible}',
        noAssignment: 'Nicht zugewiesen',
        copyFromPrevious: 'Von vorherigem kopieren',
        copyScheduleTitle: 'Zeitplan von vorherigem Termin kopieren',
        copyScheduleDescription:
            'Wähle einen vorherigen Termin aus, um dessen Zeitplanelemente zu kopieren',
        copyFromTemplateSet: 'Aus Vorlagen Gruppe übernehmen',
        copyTemplateSetTitle: 'Zeitplan aus Vorlagen Gruppe übernehmen',
        copyTemplateSetDescription:
            'Wähle eine Vorlagen Gruppe aus, um Zeitplaneinträge für diesen Termin zu erstellen',
        applyTemplateSetButton: 'Vorlagen Gruppe übernehmen',
        noTemplateSets: 'Keine Vorlagen Gruppe verfügbar',
        templateSetError: 'Vorlagen Gruppe konnte nicht hinzugefügt werden',
        itemsCount: '{count} Elemente',
        copyScheduleButton: 'Zeitplan kopieren',
        copyError: 'Fehler beim Kopieren des Zeitplans',
        noRecentSchedules: 'Keine aktuellen Zeitpläne verfügbar',
        person: 'Person',
        category: 'Kategorie',
        tagsLabel: 'Tags',
        tagsPlaceholder: 'z.B. musik, lobpreis, kinder (kommagetrennt)',
        tagsHelperText: 'Wird für Gruppierung und Autocomplete verwendet (kommagetrennt)',
        fromTemplate: 'Vorlage',
        fromHistory: 'Historie',
        clearAll: 'Alle löschen',
        clearAllTitle: 'Zeitplan leeren',
        clearAllConfirm:
            'Bist du sicher, dass du alle Zeitplan-Einträge für diesen Termin entfernen möchtest? Dies kann nicht rückgängig gemacht werden.',
        clearAllConfirmButton: 'Alle löschen',
        clearAllError: 'Zeitplan konnte nicht geleert werden',
        alreadyExists: 'Für diesen Termin existiert bereits ein Zeitplan',
        changeHistory: 'Änderungsverlauf',
        auditCreate: 'Hinzugefügt: {title}',
        auditUpdate: 'Geändert: {title}',
        auditDelete: 'Entfernt: {title}',
        auditCopy: '{count} Elemente von {fromDate} kopiert',
        auditApplyTemplateSet: 'Vorlagen Gruppe "{setName}" übernommen ({count} Elemente)',
        auditClear: 'Alle {count} Elemente gelöscht',
    },
    mailboxModal: {
        title: 'Postfach',
        subtitle: 'Ihre Nachrichten',
        recentMessages: 'Kürzliche Nachrichten',
    },
    userModal: {
        teamActivity: 'Teamaktivität',
        emailSettings: 'E-Mail-Einstellungen',
        yourEmail: 'Deine E-Mail',
        preferences: 'Einstellungen',
        autoSelectName: 'Meinen Namen automatisch auswählen',
        autoSelectDescription: 'Nicht zugewiesene Kategorien mit deinem Namen vorausfüllen',
        autoExpandEventsName: 'Termine automatisch ausklappen',
        autoExpandEventsDescription:
            'Alle Termine automatisch ausklappen (der nächste bevorstehende Termin wird immer ausgeklappt)',
        emailNotifications: 'E-Mail-Benachrichtigungen',
        nameHeader: 'Name',
        lastLoginHeader: 'Letzter Login',
        reminderCategoryName: 'Kategorien-Erinnerungen',
        reminderCategoryDescription:
            'Erhalte Erinnerungen, wenn Kategorien, die du übernehmen kannst, nicht besetzt sind.',
        notifyWhenAbsentName: 'Benachrichtigungen bei Abwesenheit',
        notifyWhenAbsentDescription:
            'E-Mail-Benachrichtigungen für Termine erhalten, an denen du als abwesend markiert bist. Deaktiviere dies, um bei Abwesenheit nicht benachrichtigt zu werden.',
        proxiesTitle: 'Stellvertreter',
        proxiesForMe: 'Meine Stellvertreter (handeln in meinem Namen)',
        iAmProxyFor: 'Ich bin Stellvertreter für',
        noProxiesForMe: 'Aktuell ist niemand als dein Stellvertreter eingerichtet.',
        notProxyForAnyone: 'Du bist aktuell für niemanden Stellvertreter.',
    },
    errors: {
        failedToSave: 'Änderungen konnten nicht gespeichert werden',
        failedToDelete: 'Element konnte nicht gelöscht werden',
        unauthorized: 'Du bist nicht berechtigt, diese Aktion auszuführen',
        failedToLoad: 'Daten konnten nicht geladen werden',
        unknownError: 'Ein unbekannter Fehler ist aufgetreten',
        errorUpdatingCategory: 'Fehler beim Aktualisieren der Kategorie: {message}',
        noPermissionEditSchedule: 'Du hast keine Berechtigung, den Zeitplan zu bearbeiten',
        noPermissionEditEvent: 'Du hast keine Berechtigung, Termine zu bearbeiten',
        failedToSaveScheduleItem: 'Zeitplan-Element konnte nicht gespeichert werden',
        failedToDeleteScheduleItem: 'Zeitplan-Element konnte nicht gelöscht werden',
        failedToReorderItems: 'Elemente konnten nicht neu angeordnet werden',
        deleteScheduleItemConfirm:
            'Bist du sicher, dass du dieses Zeitplan-Element löschen möchtest?',
    },
    install: {
        title: 'Plan Setup',
        subtitle: 'Willkommen! Lass uns dein Planungssystem konfigurieren.',
        steps: {
            basicConfig: 'Grundkonfiguration',
            authSettings: 'Authentifizierungs-Einstellungen',
            emailSettings: 'E-Mail-Einstellungen',
            calendarSettings: 'Kalender-Einstellungen',
            churchToolsIntegration: 'ChurchTools-Integration',
            scheduleFeature: 'Zeitplan-Funktion',
            databaseInstallation: 'Datenbank-Installation',
        },
        basicConfig: {
            description: 'Konfiguriere die grundlegenden Einstellungen für deine Installation.',
            domain: 'Domain',
            domainPlaceholder: 'subdomain.domain.tld',
            domainHelp: 'Die Domain, auf der diese Anwendung gehostet wird (ohne https://)',
            dbConnection: 'Datenbankverbindung',
            dbHost: 'Datenbank-Host',
            dbName: 'Datenbankname',
            dbNamePlaceholder: 'plan',
            dbUser: 'Datenbank-Benutzer',
            dbUserPlaceholder: 'root',
            dbPassword: 'Datenbank-Passwort',
            errors: {
                domainRequired: 'Domain ist erforderlich',
                dbNameRequired: 'Datenbankname ist erforderlich',
                dbUserRequired: 'Datenbank-Benutzer ist erforderlich',
            },
        },
        auth: {
            description:
                'Konfiguriere, welche Authentifizierungsmethoden für deine Benutzer verfügbar sein sollen.',
            enableAuthCode: 'Auth-Code-Login aktivieren',
            authCodeDescription:
                'Benutzern erlauben, sich mit einem 4-stelligen PIN-Code anzumelden',
            enableOidc: 'OIDC/SSO-Login aktivieren',
            authenticationMethods: 'Authentifizierungsmethoden',
            charactersEntered: '{count}/4 Zeichen eingegeben',
            invalidFormat: 'Ungültiges Format: Verwende 2 Buchstaben + 2 Ziffern (z.B. AB99)',
            validAuthCode: '✓ Gültiger Auth-Code',
            errors: {
                authCodeRequired:
                    'Admin-Auth-Code ist erforderlich, wenn Auth-Code-Login aktiviert ist',
                authCodeLength: 'Admin-Auth-Code muss genau 4 Zeichen lang sein',
                authCodeFormat:
                    'Admin-Auth-Code muss 2 Großbuchstaben gefolgt von 2 Ziffern sein (z.B. AB99)',
            },
            oidcDescription: 'Benutzern erlauben, sich über Single Sign-On anzumelden',
            adminAuthCode: 'Admin-Auth-Code',
            adminAuthCodeHelp:
                'Gib 2 Großbuchstaben (Initialen) + 2 Ziffern (Geburtsjahr) ein, z.B. AB99',
        },
        oidc: {
            description: 'Konfiguriere Single Sign-On-Authentifizierung (Optional).',
            discoveryUrl: 'OIDC Discovery URL',
            discoveryUrlPlaceholder: 'https://idp.domain.tld/.well-known/openid-configuration',
            clientId: 'Client-ID',
            clientSecret: 'Client-Secret',
            accessControl: 'Zugriffskontrolle (Optional)',
            requiredGroup: 'Erforderliche Gruppe (Optional)',
            requiredGroupPlaceholder: 'z.B. plan_users',
            requiredGroupHelp: 'Nur Benutzer in dieser Gruppe können auf die App zugreifen',
            adminGroup: 'Admin-Gruppe (Optional)',
            adminGroupPlaceholder: 'z.B. admins, administrators',
            adminGroupHelp:
                'Benutzer in dieser Gruppe erhalten automatisch Admin-Rechte beim Login via OIDC',
        },
        mail: {
            description: 'E-Mail-Benachrichtigungen konfigurieren (Optional).',
            enable: 'E-Mail-Postfach aktivieren',
            host: 'Mailserver-Host',
            port: 'Mailserver-Port',
            username: 'Mail-Benutzername',
            usernamePlaceholder: 'mail@domain.tld',
            password: 'Mail-Passwort',
            emailHeaders: 'E-Mail-Header',
            sender: 'Absender-E-Mail',
            senderPlaceholder: 'noreply@domain.tld',
            replyTo: 'Antwort-an-E-Mail',
            replyToPlaceholder: 'support@domain.tld',
            errors: {
                hostRequired: 'Mailserver-Host ist erforderlich, wenn E-Mail aktiviert ist',
                portRequired:
                    'Gültiger Mailserver-Port ist erforderlich, wenn E-Mail aktiviert ist',
                usernameRequired: 'Mail-Benutzername ist erforderlich, wenn E-Mail aktiviert ist',
                passwordRequired: 'Mail-Passwort ist erforderlich, wenn E-Mail aktiviert ist',
                senderRequired: 'Absender-E-Mail ist erforderlich, wenn E-Mail aktiviert ist',
            },
        },
        calendar: {
            description: 'Kalender-Export-Einstellungen konfigurieren.',
            eventName: 'Ereignisname',
            eventNamePlaceholder: 'Sonntagsgottesdienst',
            eventLocation: 'Veranstaltungsort',
            eventLocationPlaceholder: 'Kirchengebäude',
            startTime: 'Standard-Startzeit',
            endTime: 'Standard-Endzeit',
        },
        churchTools: {
            description: 'Integration mit ChurchTools für Agenda- und Dateiverwaltung (Optional).',
            enable: 'ChurchTools-Integration aktivieren',
            apiUrl: 'ChurchTools API URL',
            apiUrlPlaceholder: 'https://ihrekirche.church.tools/api/',
            apiToken: 'ChurchTools API Token',
            enableCategories: 'Kategorien-Synchronisation aktivieren',
            enableAgenda: 'Agenda-Integration aktivieren',
            enableFiles: 'Dateiverwaltung aktivieren',
            errors: {
                urlRequired:
                    'ChurchTools API URL ist erforderlich, wenn ChurchTools-Integration aktiviert ist',
                tokenRequired:
                    'ChurchTools API Token ist erforderlich, wenn ChurchTools-Integration aktiviert ist',
            },
        },
        schedule: {
            description: 'Zeitplan-Verwaltungsfunktion konfigurieren.',
            enable: 'Zeitplan-Funktion aktivieren',
            minDuration: 'Mindestdauer (Minuten)',
            defaultStartTime: 'Standard-Startzeit',
            defaultSetupTime: 'Standard-Aufbauzeit (Minuten)',
            defaultTeardownTime: 'Standard-Abbauzeit (Minuten)',
        },
        database: {
            description:
                'Die Konfigurationsdatei wurde erfolgreich erstellt. Klicke auf die Schaltfläche unten, um das Datenbankschema zu installieren.',
            warningTitle: 'Hinweis:',
            warningMessage:
                'Dies erstellt alle erforderlichen Tabellen in deiner Datenbank. Stelle sicher, dass die Datenbank leer ist oder du ein Backup hast.',
            installButton: 'Datenbank installieren',
            installing: 'Installiere ...',
        },
        buttons: {
            next: 'Weiter',
            createConfiguration: 'Konfiguration erstellen',
            creatingConfiguration: 'Erstelle Konfiguration ...',
        },
        messages: {
            pleaseFixErrors: 'Bitte behebe die folgenden Fehler:',
            configTooltip: 'Bitte behebe alle Fehler, bevor du die Konfiguration erstellst',
        },
    },
    login: {
        title: 'Plan',
        subtitle: 'Melde dich an, um fortzufahren',
        codeTab: 'Code',
        ssoTab: 'SSO',
        codePrompt: 'Gib deinen 4-stelligen Zugangscode ein',
        signIn: 'Anmelden',
        signingIn: 'Anmelden ...',
        ssoPrompt: 'Melde dich mit deinem IDP Konto an',
        signInWithSSO: 'Mit SSO anmelden',
        poweredBy: 'Betrieben mit OIDC',
        enterFourDigits: 'Bitte gib einen 4-stelligen Code ein',
        loginFailed: 'Anmeldung fehlgeschlagen: {error}',
        invalidCode: 'Unbekannter Code',
        tooManyAttempts: 'Zu viele Fehlversuche. Bitte warte 15 Minuten und versuche es erneut.',
        codeLoginDisabled: 'Die Anmeldung mit Code ist deaktiviert.',
    },
    unauthorized: {
        title: 'Zugriff verweigert',
        subtitle: 'Du hast keine Berechtigung, auf diese Anwendung zuzugreifen',
        authStatusSuccess: 'Authentifizierungsstatus',
        authStatusDenied: 'Autorisierungsstatus',
        authSuccess: '✓ Erfolgreich',
        authDenied: '✗ Verweigert',
        defaultMessage:
            'Deine Authentifizierung war erfolgreich, aber du bist nicht berechtigt, auf diese Anwendung zuzugreifen.',
        accountInactive: 'Dein Konto wurde deaktiviert. Bitte wende dich an einen Administrator.',
        requiredGroup: 'Erforderliche Gruppe',
        yourGroups: 'Deine Gruppen',
        whatCanIDo: 'Was kann ich tun?',
        step1: 'Kontaktiere deinen Systemadministrator',
        step2: 'Fordere Zugriff auf die erforderliche Gruppe an',
        step3: 'Warte auf die Genehmigung der Gruppenmitgliedschaft',
        step4: 'Versuche dich erneut anzumelden',
        backToLogin: 'Zurück zur Anmeldung',
    },
    migrations: {
        title: 'Datenbankmigrationen',
        subtitle: 'Verwalte Datenbankschemata und Aktualisierungen',
        migrations: 'Migrationen',
        migration: 'Migration',
        status: 'Status',
        actions: 'Aktionen',
        applied: 'Angewendet',
        pending: 'Ausstehend',
        apply: 'Anwenden',
        applyAll: 'Alle ausstehenden anwenden',
        noneFound: 'Keine Migrationen im Migrationsverzeichnis gefunden.',
        confirmTitle: 'Migration bestätigen',
        confirmAll:
            'Bist du sicher, dass du alle ausstehenden Migrationen anwenden möchtest? Diese Aktion kann nicht rückgängig gemacht werden.',
        confirmOne: 'Bist du sicher, dass du die Migration "{migration}" anwenden möchtest?',
        success: 'Migration "{migration}" erfolgreich angewendet!',
        allSuccess: 'Alle Migrationen erfolgreich angewendet!',
        someFailed: 'Einige Migrationen sind fehlgeschlagen',
        failed: 'Migration fehlgeschlagen',
        failedApply: 'Migration konnte nicht angewendet werden',
        failedLoad:
            'Migrationsstatus konnte nicht geladen werden. Bitte überprüfe deine Verbindung und versuche es erneut.',
        upToDate: 'Datenbank ist auf dem neuesten Stand! Alle Migrationen wurden angewendet.',
        pendingTitle: 'Ausstehende Migrationen:',
        pendingCount: 'Migration(en) müssen angewendet werden.',
    },
    categoryRequests: {
        title: 'Kategorienanfragen',
        requestJoin: 'Kategorie beitreten',
        requestLeave: 'Kategorie verlassen',
        yourRequests: 'Deine Anfragen',
        pendingRequests: 'Ausstehende Anfragen',
        deniedRequests: 'Abgelehnte Anfragen',
        noRequests: 'Keine Anfragen gefunden',
        noRequestsDescription: 'Es gibt derzeit keine Kategoriezuweisungsanfragen.',
        noPendingRequests: 'Keine ausstehenden Anfragen',
        requestType: 'Anfragetyp',
        join: 'Beitreten',
        leave: 'Verlassen',
        pending: 'Ausstehend',
        accepted: 'Akzeptiert',
        denied: 'Abgelehnt',
        requester: 'Antragsteller',
        category: 'Kategorie',
        requestDate: 'Antragsdatum',
        message: 'Nachricht',
        adminNote: 'Admin-Notiz',
        accept: 'Akzeptieren',
        deny: 'Ablehnen',
        selected: '{count} ausgewählt',
        selectCategory: 'Kategorie auswählen',
        selectCategoryHelp: 'Wähle die Kategorie, der du beitreten oder die du verlassen möchtest',
        requestMessage: 'Nachricht (Optional)',
        requestMessageHelp: 'Füge deiner Anfrage eine Nachricht hinzu',
        adminNoteLabel: 'Admin-Notiz (Optional)',
        adminNoteHelp: 'Füge eine für den Antragsteller sichtbare Notiz hinzu',
        confirmAccept: 'Bist du sicher, dass du diese Anfrage akzeptieren möchtest?',
        confirmDeny: 'Bist du sicher, dass du diese Anfrage ablehnen möchtest?',
        errorCreate: 'Fehler beim Erstellen der Anfrage: {message}',
        errorWithdraw: 'Fehler beim Zurückziehen der Anfrage: {message}',
        reviewedBy: 'Überprüft von {name} am {date}',
        pendingCount: '{count} ausstehend',
        filterAll: 'Alle',
        filterPending: 'Ausstehend',
        filterAccepted: 'Akzeptiert',
        filterDenied: 'Abgelehnt',
        youAreAlreadyMember: 'Du bist bereits Mitglied dieser Kategorie',
        youAreNotMember: 'Du bist kein Mitglied dieser Kategorie',
        request: 'Anfrage',
        answer: 'Antwort',
    },
    welcome: {
        title: 'Willkommen!',
        message:
            'Schön, dass du da bist! Es scheint, dass du zurzeit noch keiner Kategorie zugewiesen bist. Du kannst eigenständig Kategorien anfragen. Möchtest du jetzt Kategorien anfragen?',
        requestCategories: 'Kategorien anfragen',
        remindLater: 'Erinnere mich später',
        dismiss: 'Nicht mehr anzeigen',
        pendingMessage:
            'Du hast erfolgreich Kategorien angefragt. Diese sind aktuell ausstehend und müssen noch bestätigt werden.',
    },
    oidcAuth: {
        invalidState: 'Ungültiger Status-Parameter',
        tokenExchangeFailed: 'Token-Austausch fehlgeschlagen',
        userinfoFailed: 'Benutzerinfo konnte nicht abgerufen werden – fehlende Kennung',
        accessDenied:
            'OIDC-Zugriff verweigert für Benutzer {sub}. Erforderliche Gruppe: {group}. Benutzergruppen: {groups}',
        failedCreateOrUpdateUser: 'Benutzer konnte nicht erstellt oder aktualisiert werden',
        failedLoadUserSession: 'Benutzersitzung konnte nicht geladen werden',
        authenticationFailed: 'Authentifizierung fehlgeschlagen: {message}',
    },
    metricsPage: {
        title: 'Metriken',
        subtitle: 'Übersicht über Aktivität, Benutzer, Kategorien, Zuweisungen und Kommunikation',
        openChangeRequests: '{count} offene Tauschanfrage{{n}}',
        pendingCategoryRequests: '{count} ausstehende Kategorienanfrage{{n}}',
        filteredByUser: 'Nach Benutzer gefiltert',
        userFilter: {
            label: 'Nach Benutzer filtern',
            allUsers: 'Alle Benutzer',
            placeholder: 'Benutzer suchen…',
        },
        dateRange: {
            label: 'Zeitraum',
            from: 'Von',
            to: 'Bis',
            last30: 'Letzte 30 Tage',
            last90: 'Letzte 90 Tage + 30 Zukunft',
            last180: 'Letzte 180 Tage',
            thisYear: 'Dieses Jahr',
        },
        sections: {
            activity: 'Aktivität',
            users: 'Benutzer',
            categories: 'Kategorien',
            assignments: 'Zuweisungen',
            events: 'Termine',
            communication: 'Kommunikation & Anfragen',
            topUsers: 'Meisteingeteilte Benutzer',
            usagePatterns: 'Nutzungsmuster',
            schedule: 'Zeitplan',
            screenshots: 'Debug-Screenshots',
        },
        stats: {
            loginsInRange: 'Anmeldungen im Zeitraum',
            loginsFailed: 'Fehlgeschlagene Anmeldeversuche',
            weeklyLogins: 'Anmeldungen diese Woche',
            monthlyLogins: 'Anmeldungen diesen Monat',
            modalOpensTotal: 'Geöffnete Dialoge',
            pdfCreated: 'PDFs erstellt',
            planViewsTotal: 'Plan-Aufrufe',
            planViewsWithHistory: 'Aufrufe mit Verlauf',
            planViewsAvgForecast: 'Ø geladene Termine',
            historyOpened: 'Verlauf geöffnet',
            filterChanged: 'Kategoriefilter-Änderungen',
            totalCategories: 'Kategorien gesamt',
            activeCategories: 'Aktive Kategorien',
            categoryGroups: 'Kategoriengruppen',
            avgMembersPerCategory: 'Ø Mitglieder / Kategorie',
            totalAssignments: 'Zuweisungen gesamt',
            assignmentsInRange: 'Zuweisungen im Zeitraum',
            assignmentsLast30Days: 'Zuweisungen (30 Tage)',
            openChangeRequests: 'Offene Tauschanfragen',
            pendingCategoryRequests: 'Ausstehende Kategorienanfragen',
            totalEvents: 'Termine gesamt',
            activeEvents: 'Aktive Termine',
            upcomingEvents: 'Bevorstehende Planeinträge',
            next90Days: '(nächste 90 Tage)',
            absencesTotal: 'Abwesenheiten',
            scheduleItemsCreated: 'Zeitplanpunkte erstellt',
            scheduleItemsUpdated: 'Zeitplanpunkte aktualisiert',
            scheduleItemsDeleted: 'Zeitplanpunkte gelöscht',
            scheduleClearedAll: 'Kompletter Zeitplan geleert (Admin)',
            scheduleCopied: 'Zeitpläne kopiert',
            scheduleTemplateSetApplied: 'Vorlagensets angewendet',
            scheduleTemplatesTotal: 'Vorlagen gesamt',
            scheduleTemplateSetsTotal: 'Vorlagensets gesamt',
            screenshotsTaken: 'Screenshots hochgeladen',
        },
        charts: {
            loginsPerDay: 'Anmeldungen pro Tag',
            loginsLabel: 'Anmeldungen',
            loginsFailedLabel: 'Fehlgeschlagen',
            failedByReason: 'Fehlversuche nach Grund',
            failedTopCodes: 'Häufig versuchte Codes',
            modalOpens: 'Geöffnete Dialoge',
            pdfOptions: 'PDF-Optionen',
            planViewsPerDay: 'Plan-Aufrufe pro Tag',
            planViews: 'Plan-Aufrufe',
            planViewsLabel: 'Aufrufe',
            userBreakdown: 'Benutzerstatus',
            userRoles: 'Benutzer nach Rolle',
            membersPerCategory: 'Mitglieder pro Kategorie',
            categoryStatus: 'Kategoriestatus',
            assignmentsPerMonth: 'Zuweisungen pro Monat',
            assignmentsLabel: 'Zuweisungen',
            topCategoriesByAssignments: 'Top-Kategorien nach Zuweisungen',
            topUsersByAssignments: 'Top-Benutzer nach Zuweisungen',
            topUsersByLogins: 'Top-Benutzer nach Anmeldungen',
            changeRequestOutcomes: 'Ergebnisse der Tauschanfragen',
            changeRequestsPerDay: 'Tauschanfragen pro Tag',
            changeRequestsCreatedLabel: 'Erstellt',
            changeRequestsCancelledLabel: 'Abgebrochen',
            changeRequestsAcceptedLabel: 'Akzeptiert',
            categoryRequestOutcomes: 'Ergebnisse der Kategorienanfragen',
            categoryRequestsPerDay: 'Kategorieanfragen pro Tag',
            categoryRequestsAcceptedLabel: 'Akzeptiert',
            categoryRequestsDeniedLabel: 'Abgelehnt',
            categoryRequestsPendingLabel: 'Ausstehend',
            noReminders: 'Keine Erinnerungen',
            mailsPerDay: 'Versendete Mails pro Tag',
            mailsRemindersLabel: 'Erinnerungen',
            mailsNotificationsLabel: 'Benachrichtigungen',
            reminderIntervals: 'Konfigurierte Erinnerungsintervalle',
            reminderIntervalLabel: '{days} Tag{{e}}',
            absencesOverTime: 'Abwesenheiten im Zeitverlauf',
            themeDistribution: 'Theme-Verteilung',
            lastLoginRecency: 'Letzte Anmeldung der Benutzer',
            lastLoginLast7d: 'Innerhalb 7 Tage',
            lastLoginLast30d: 'Vor 8 – 30 Tagen',
            lastLoginLast90d: 'Vor 31 – 90 Tagen',
            lastLoginOlder: 'Vor mehr als 90 Tagen',
            historyUsage: 'Verlauf geöffnet pro Tag',
            languageDistribution: 'Sprachverteilung',
            scheduleItemsPerDay: 'Zeitplanpunkt-Aktivität pro Tag',
            scheduleTopTemplateSets: 'Häufig angewendete Vorlagensets',
            scheduleTopTemplatesUsed: 'Häufig verwendete Zeitplanvorlagen',
            scheduleTemplateCrud: 'Vorlagen- & Set-Änderungen',
            screenshotsPerDay: 'Screenshots pro Tag',
            screenshotsByDevice: 'Screenshots nach Gerät',
            screenshotsByBrowser: 'Screenshots nach Browser',
            screenshotsByWidth: 'Screenshots nach Bildschirmbreite',
            viewDaily: 'Täglich',
            viewWeekly: 'Nach Wochentag',
        },
        pie: {
            active: 'Aktiv',
            inactive: 'Inaktiv',
            activeCategories: 'Aktiv',
            inactiveCategories: 'Inaktiv',
            crAccepted: 'Akzeptiert (ersetzt)',
            crCancelled: 'Abgebrochen',
        },
        roles: {
            scheduleEditors: 'Zeitplan-Editoren',
            eventEditors: 'Termin-Editoren',
        },
    },
    handbook: {
        title: 'Handbuch',
        menuLabel: 'Handbuch',
        groups: {
            general: 'Allgemein',
            extended: 'Funktionen',
            schedule: 'Ablaufplan',
            admin: 'Administration',
        },
        badges: {
            admin: 'Nur für Admins',
            scheduleEditor: 'Ablaufplan-Editor',
            scheduleTemplateEditor: 'Vorlagen-Editor',
            responsible: 'Kategorie-Verantwortlicher',
            proxy: 'Stellvertreter',
        },
        sections: {
            overview: 'Übersicht',
            events: 'Termine',
            absent: 'Abwesenheit melden',
            filter: 'Kategorie-Filter',
            history: 'Verlauf',
            forecast: 'Angezeigte Termine',
            profile: 'Profil & Einstellungen',
            theme: 'Hell / Dunkel-Modus',
            yearSummary: 'Jahresübersicht',
            personalForecast: 'Mein Dienstplan',
            changeRequests: 'Tauschanfragen',
            categoryMemberships: 'Kategorie-Mitgliedschaften',
            mailbox: 'Postfach',
            proxy: 'Verwaltung für andere',
            responsible: 'Anfragen prüfen',
            scheduleViewing: 'Ablaufplan ansehen',
            scheduleEditing: 'Ablaufplan bearbeiten',
            scheduleTemplates: 'Ablaufvorlagen',
            adminUsers: 'Benutzerverwaltung',
            adminCategories: 'Kategorieverwaltung',
            adminEvents: 'Terminverwaltung',
            adminMetrics: 'Metriken-Dashboard',
            adminLogs: 'Systemprotokolle',
        },
        overview: {
            intro: 'Willkommen bei Plan – deinem Dienstplanungstool. Dieses Handbuch erklärt die Funktionen, die dir basierend auf deiner Rolle zur Verfügung stehen.',
            navigation:
                'Nutze die Seitenleiste links, um zu einem Thema zu springen. Die Abschnitte sind nach Thema und Rolle gruppiert. Es werden nur die für dein Konto relevanten Abschnitte angezeigt.',
            tip: 'Tipp: Du kannst dieses Handbuch jederzeit über das Menü öffnen.',
        },
        events: {
            what: 'Die Hauptseite zeigt eine Liste der bevorstehenden Gottesdienst-Termine. Jede Karte steht für ein Datum und zeigt den Termin-Namen, Ort, Zeit und die zugehörigen Kategorien.',
            expand: 'Tippe auf eine Terminkarte, um sie aus- oder einzuklappen. Der aktuell hervorgehobene Termin (heute oder der nächste bevorstehende) wird automatisch geöffnet.',
            categories:
                'In einem aufgeklappten Termin siehst du alle Dienstkategorien und wer diesen zugewiesen ist. Deine eigenen Zuweisungen sind visuell hervorgehoben.',
            canceled:
                'Abgesagte Termine werden mit einem speziellen Symbol angezeigt und sind standardmäßig eingeklappt.',
            tip: 'Tipp: Nutze den Regler "Angezeigte Termine" im Menü, um festzulegen, wie viele bevorstehende Termine angezeigt werden.',
        },
        absent: {
            what: 'Du kannst dich für ein bestimmtes Datum als abwesend markieren, wenn du nicht verfügbar bist.',
            how: 'Öffne eine Terminkarte und tippe auf die Schaltfläche "Abwesend". Deine Abwesenheit wird sofort gespeichert und ist für Admins sichtbar.',
            restriction:
                'Wenn du an diesem Datum bereits einer Kategorie zugewiesen bist, musst du zuerst eine Tauschanfrage stellen oder einen Admin bitten, dich umzuteilen, bevor du dich als abwesend markieren kannst.',
            tip: 'Tipp: Deine Abwesenheit wird mit einem Flugzeug-Symbol auf der Terminkarte angezeigt, damit du schnell siehst, welche Termine du markiert hast.',
        },
        filter: {
            what: 'Mit dem Kategorie-Filter kannst du auswählen, welche Dienstkategorien auf den Terminkarten angezeigt werden. Das hilft dir, dich auf das Wesentliche zu konzentrieren.',
            how: 'Öffne den Filter über das Menü. Aktiviere oder deaktiviere Kategorien und tippe auf "Anwenden". Deine Einstellungen werden gespeichert und bleiben sitzungsübergreifend erhalten.',
            assigned:
                'Kategorien, denen du zugewiesen bist, sind immer sichtbar und können nicht ausgeblendet werden – sie werden im Filter mit einem Schloss-Symbol angezeigt.',
            tip: 'Tipp: Du kannst den gesamten Filter über das Filter-Symbol auf jeder Terminkarte schnell ein- oder ausschalten, ohne den Filter-Dialog zu öffnen.',
        },
        history: {
            what: 'Standardmäßig werden nur bevorstehende Termine angezeigt. Wenn du den Verlauf aktivierst, werden vergangene Termine oberhalb des aktuellen Datums eingeblendet.',
            how: 'Schalte den Verlauf über "Verlauf anzeigen" / "Verlauf ausblenden" im Menü um.',
            tip: 'Tipp: Der Verlauf wird beim Neuladen der Seite wieder deaktiviert.',
        },
        forecast: {
            what: 'Der Regler "Angezeigte Termine" im Menü legt fest, wie viele zukünftige Termine geladen werden. Ein kleinerer Wert hält die Liste kurz; ein größerer zeigt mehr Termine.',
            slider: 'Ziehe den Regler zwischen 5 und 45 Termine. Die Liste aktualisiert sich automatisch, wenn du den Regler loslässt.',
            tip: 'Tipp: Bei vielen Terminen beschleunigt ein kleinerer Wert das Laden.',
        },
        profile: {
            what: 'Öffne dein Profil über das Menü, um deine persönlichen Einstellungen zu verwalten.',
            email: 'Du kannst hier deine Benachrichtigungs-E-Mail-Adresse aktualisieren. Diese Adresse wird für Erinnerungsmails verwendet.',
            language:
                'Wechsle die Anwendungssprache zwischen Englisch und Deutsch. Die Änderung ist sofort wirksam.',
            preselect:
                'Wenn "Vorauswahl" aktiviert ist, wirst du automatisch in nicht besetzten Kategorien vorausgewählt, wenn der Plan befüllt wird.',
            autoExpand:
                'Wenn "Termine automatisch aufklappen" aktiviert ist, werden alle Terminkarten standardmäßig geöffnet statt nur der aktuelle.',
            reminders:
                'Konfiguriere Erinnerungsmails: Wähle, ob du 1, 2, 3 oder 7 Tage vor einem Dienst, zu dem du eingeteilt bist, eine Erinnerung erhalten möchtest. Du kannst auch festlegen, ob die Erinnerungen deine Kategorieeinteilung enthalten sollen.',
            notifyAbsent:
                '"Benachrichtigungen bei Abwesenheit" steuert, ob du weiterhin E-Mail-Benachrichtigungen (z. B. "Hilfe benötigt"-Hinweise oder Tauschanfragen-Benachrichtigungen) für Termine erhältst, an denen du als abwesend markiert bist. Standardmäßig ist die Option aktiviert. Deaktiviere sie, wenn du für Termine, die du bereits blockiert hast, nicht kontaktiert werden möchtest.',
            tip: 'Tipp: Alle Änderungen im Profil werden automatisch gespeichert, sobald du das Feld verlässt.',
        },
        theme: {
            what: 'Du kannst zwischen dem dunklen und hellen Modus wechseln, um deiner persönlichen Vorliebe oder den Lichtverhältnissen zu entsprechen.',
            how: 'Verwende das Sonnen-/Mond-Symbol in der oberen rechten Ecke der App-Leiste, um den Modus umzuschalten. Die Einstellung wird in deinem Konto gespeichert.',
        },
        yearSummary: {
            what: 'Die Jahresübersicht bietet eine druckbare Übersicht aller Termine eines ausgewählten Jahres mit Datum, Uhrzeit, Wochentag und Ort.',
            how: 'Öffne sie über das Menü und wähle das gewünschte Jahr. Die Tabelle kann in die Zwischenablage kopiert werden, um sie in andere Dokumente einzufügen.',
            tip: 'Tipp: Verwende die Schaltfläche "Kopieren", um die Daten schnell in eine Tabellenkalkulation oder E-Mail einzufügen.',
        },
        personalForecast: {
            what: '"Mein Dienstplan" zeigt dir alle bevorstehenden Diensttermine an einem Ort. Du kannst wählen, wie viele zukünftige Einträge geladen werden.',
            changeRequest:
                'Von dieser Ansicht aus kannst du für eine bevorstehende Einteilung eine Tauschanfrage stellen, wenn du verhindert bist. Eine Tauschanfrage signalisiert Admins und anderen Teammitgliedern, dass du eine Vertretung benötigst.',
            cancelRequest:
                'Du kannst eine ausstehende Tauschanfrage hier auch wieder zurückziehen, wenn sich deine Pläne ändern.',
            tip: 'Tipp: Sobald eine Tauschanfrage von einem Admin akzeptiert wird, wirst du automatisch aus dieser Einteilung entfernt.',
        },
        changeRequests: {
            what: 'Tauschanfragen sind eine Möglichkeit, zu signalisieren, dass du einen eingeplanten Dienst nicht wahrnehmen kannst und eine Vertretung benötigst.',
            how: 'Öffne eine Terminkarte und tippe auf das Tausch-Symbol bei deiner zugewiesenen Kategorie. Du kannst Anfragen auch über "Mein Dienstplan" stellen.',
            outcome:
                'Ein Admin überprüft offene Tauschanfragen und weist entweder eine Vertretung zu (Akzeptieren) oder bricht sie ab. Du wirst benachrichtigt, wenn sich der Status ändert.',
            tip: 'Tipp: Du kannst alle offenen Tauschanfragen für einen Termin über die Tausch-Schaltfläche auf der Terminkarte sehen.',
        },
        categoryMemberships: {
            what: '"Meine Kategorie-Mitgliedschaften" ermöglicht es dir, den Beitritt zu Dienstkategorien zu beantragen oder sie zu verlassen.',
            join: 'Wechsle zum Tab "Beitreten", wähle die Kategorien, denen du angehören möchtest, füge eine optionale Nachricht hinzu und sende den Antrag ab. Ein Admin oder der Kategorie-Verantwortliche prüft deinen Antrag.',
            leave: 'Wechsle zum Tab "Verlassen", um den Austritt aus Kategorien zu beantragen, in denen du nicht mehr dienen möchtest.',
            pending:
                'Du kannst alle deine ausstehenden Anfragen im gleichen Dialog sehen und bei Bedarf zurückziehen.',
            tip: 'Tipp: Der Beitritt zu einer Kategorie bedeutet nicht, dass du automatisch zu Diensten eingeteilt wirst – Einteilungen werden von Admins verwaltet.',
        },
        mailbox: {
            what: 'Das Postfach zeigt eingehende E-Mails im Zusammenhang mit deiner Dienstplanung, z. B. Antworten oder Benachrichtigungen an die Plan-Adresse.',
            reminders:
                'Erinnerungsmails für deine bevorstehenden Einteilungen werden ebenfalls hier konfiguriert. Aktiviere die gewünschten Intervalle in deinen Profileinstellungen.',
            tip: 'Tipp: Das Postfach zeigt nur Nachrichten, die beim gemeinsam genutzten Plan-Postfach eingegangen sind, nicht deine persönliche E-Mail.',
        },
        proxy: {
            what: 'Du wurdest als Stellvertreter für ein oder mehrere andere Teammitglieder eingerichtet. Das bedeutet, dass du deren Diensteinteilungen in ihrem Namen verwalten kannst – sie haben möglicherweise keinen eigenen Zugang zu Plan.',
            sees: 'Alle Kategorien, denen die Personen, die du vertrittst, zugewiesen sind, erscheinen in deiner Hauptansicht – genau wie deine eigenen Kategorien. Sie sind visuell nicht von deinen persönlichen Kategorien zu unterscheiden.',
            cando: 'Als Stellvertreter kannst du: die vertretene Person einem freien Kategorie-Slot zuteilen, sie aus einem Slot entfernen sowie Tauschanfragen für ihre Einteilungen stellen und zurückziehen.',
            cantdo: 'Du kannst die vertretene Person nicht als abwesend markieren, ihre Profileinstellungen ändern oder ihre persönlichen Nachrichten einsehen. Aktionen, die du durchführst, werden im Protokoll als deine eigene Aktivität erfasst.',
            tip: 'Tipp: Wenn du nicht sicher bist, welche Kategorien zu wem gehören, prüfe den zugewiesenen Namen in der jeweiligen Kategoriezeile, um zu erkennen, wessen Slot es ist.',
        },
        responsible: {
            what: 'Als Kategorie-Verantwortlicher bist du die Ansprechperson für eine oder mehrere Dienstkategorien. Du kannst Mitgliedschaftsanfragen anderer Benutzer für diese Kategorien prüfen.',
            review: 'Öffne "Kategorie-Anfragen" über das Menü. Du siehst alle ausstehenden Anfragen für deine Kategorien. Du kannst jede Anfrage mit einer optionalen Notiz akzeptieren oder ablehnen.',
            badge: 'Ein Badge auf dem Menüpunkt zeigt die Anzahl der offenen Anfragen, die auf deine Prüfung warten.',
            tip: 'Tipp: Das Akzeptieren einer Mitgliedschaftsanfrage fügt die Person nur zur Kategorie hinzu – du musst sie weiterhin für spezifische Termine einteilen.',
        },
        scheduleViewing: {
            what: 'Wenn für einen Termin ein Ablaufplan erstellt wurde, erscheint ein Ablaufplan-Symbol auf der Terminkarte. Tippe darauf, um den Ablaufplan zu öffnen.',
            open: 'Der Ablaufplan zeigt den Programmablauf: Titel, Dauer, Verantwortliche und Notizen für jeden Punkt.',
            pdf: 'Du kannst den Ablaufplan direkt aus der Ablaufplan-Ansicht als PDF herunterladen.',
            tip: 'Tipp: Der Ablaufplan ist für normale Benutzer schreibgeschützt. Zum Bearbeiten ist die Rolle "Ablaufplan-Editor" erforderlich.',
        },
        scheduleEditing: {
            what: 'Als Ablaufplan-Editor kannst du den Ablaufplan für jeden Termin erstellen und verwalten.',
            add: 'Öffne den Ablaufplan für einen Termin und verwende die Schaltfläche "Punkt hinzufügen", um einen neuen Eintrag zu erstellen. Fülle Titel, Dauer, Verantwortlichen und optionale Notizen aus.',
            template:
                'Beim Hinzufügen von Punkten kannst du aus vorhandenen Vorlagen wählen – tippe einfach in das Titelfeld und passende Vorlagen werden vorgeschlagen.',
            reorder:
                'Ziehe Ablaufplan-Punkte per Drag & Drop, um sie innerhalb der Liste neu anzuordnen.',
            copy: 'Verwende "Von vorherigem kopieren", um den gesamten Ablaufplan eines vergangenen Datums zu kopieren, anstatt ihn neu zu erstellen.',
            pdf: 'Erstelle und lade ein PDF des Ablaufplans herunter – mit optionalen Einstellungen für Hervorhebungen, Notizen und Dauer.',
            tip: 'Tipp: Verwende Tags auf Ablaufplan-Punkten, um sie später über Termine hinweg zu gruppieren und zu suchen.',
        },
        scheduleTemplates: {
            what: 'Ablaufvorlagen sind wiederverwendbare Einträge, die die Ablaufplan-Erstellung beschleunigen. Vorlagensets sind benannte Sammlungen von Vorlagen, die mit einem Klick auf ein Datum angewendet werden können.',
            templates:
                'Verwalte einzelne Vorlagen über den Menüpunkt "Ablaufvorlagen". Jede Vorlage hat einen Namen, eine Dauer, eine optionale verantwortliche Person und Tags.',
            sets: 'Vorlagensets fassen mehrere Vorlagen zusammen. Öffne "Ablaufvorlagen" über das Menü, um Sets zu erstellen und zu organisieren.',
            apply: 'Beim Bearbeiten eines Ablaufplans kannst du mit "Vorlagenset anwenden" alle Punkte eines Sets auf einmal einfügen. Die Punkte werden nach vorhandenen Einträgen angefügt.',
            tip: 'Tipp: Vorlagen mit Tags eignen sich gut für die tag-basierte Suche beim Hinzufügen einzelner Punkte.',
        },
        adminUsers: {
            what: 'Im Admin-Bereich kannst du alle Benutzerkonten verwalten: neue Benutzer anlegen, Rollen vergeben, Kategorie-Zuweisungen verwalten und Stellvertreter konfigurieren.',
            roles: 'Verfügbare Rollen: Admin (Vollzugriff), Ablaufplan-Editor (kann Ablaufpläne bearbeiten), Ablaufvorlagen-Editor (kann Vorlagen und Sets verwalten), Termin-Editor (kann Termine erstellen und bearbeiten).',
            categories:
                'Weise Benutzer direkt über die Benutzerkarte im Admin-Bereich Kategorien zu. Du kannst einen Benutzer auch als Assistenten innerhalb einer Kategorie markieren oder einen Benachrichtigungsschwellenwert festlegen.',
            proxy: 'Ein Stellvertreter ermöglicht es einem aktiven Benutzer, Diensteinteilungen im Namen eines anderen Benutzers zu verwalten. Um einen einzurichten, öffne eine Benutzerkarte im Admin-Bereich und füge unter "Stellvertreter" einen Benutzer hinzu. Der Stellvertreter sieht dann automatisch alle Kategorien, denen die vertretene Person zugewiesen ist, und kann für sie einteilen, austeilen sowie Tauschanfragen stellen und zurückziehen – als würde er in deren Namen handeln. Das ist ideal für Mitglieder, die ihren Dienstplan nicht selbst verwalten können (z. B. Kinder, ältere Mitglieder oder Personen ohne Gerätezugang). Ein Benutzer kann Stellvertreter für mehrere Personen sein, und eine Person kann mehrere Stellvertreter haben.',
            authCode:
                'Wenn die Auth-Code-Anmeldung aktiviert ist, hat jeder Benutzer einen 4-stelligen Code für die schnelle Anmeldung. Du kannst diesen Code im Admin-Bereich festlegen oder zurücksetzen.',
            tip: 'Tipp: Das Deaktivieren eines Benutzers behält dessen Verlauf, verhindert aber die Anmeldung.',
        },
        adminCategories: {
            what: 'Die Kategorieseite ermöglicht das Erstellen, Bearbeiten und Deaktivieren von Dienstkategorien.',
            settings:
                'Für jede Kategorie kannst du konfigurieren: Name, Symbol, Gruppe, Auf-/Abbauzugabe, Min./Max.-Einteilungen und ob mehrere Personen gleichzeitig eingeteilt werden können.',
            responsible:
                'Du kannst einen oder mehrere Kategorie-Verantwortliche festlegen, die Mitgliedschaftsanfragen für diese Kategorie prüfen können, ohne vollen Admin-Zugriff zu benötigen.',
            exclusions:
                'Kategorien können von bestimmten Serienterminen ausgeschlossen werden, wenn sie für bestimmte Termintypen nicht benötigt werden.',
            tip: 'Tipp: Inaktive Kategorien werden vor Benutzern verborgen, können aber jederzeit reaktiviert werden, ohne dass der Einteilungsverlauf verloren geht.',
        },
        adminEvents: {
            what: 'Termine sind die wiederkehrenden Gottesdienst-Daten (z. B. "Sonntagsgottesdienst"). Sie definieren das Wiederholungsmuster und erscheinen automatisch im Plan entsprechend ihrer Regel.',
            recurrence:
                'Termine können wöchentlich (an bestimmten Wochentagen) oder monatlich (in einer bestimmten Woche und an einem bestimmten Tag) wiederkehren. Jedes Vorkommen kann einen eigenen Namen, Ort und Uhrzeit haben.',
            cancel: 'Ein einzelnes Vorkommen kann als abgesagt markiert werden, ohne den Termin zu löschen. Abgesagte Daten sind weiterhin im Plan mit einem speziellen Symbol sichtbar.',
            tip: 'Tipp: Eine Kategorie von einem Termin auszuschließen bedeutet, dass diese Kategorie bei keinem Vorkommen dieses Termins erscheint.',
        },
        adminMetrics: {
            what: 'Das Metriken-Dashboard gibt dir einen Überblick darüber, wie Plan genutzt wird: Anmeldungen, Einteilungen, Ablaufplan-Aktivität, Kommunikation und mehr.',
            sections:
                'Metriken sind in Abschnitte unterteilt: Aktivität, Nutzungsmuster, Benutzer, Einteilungen, Ablaufplan und Kommunikation. Jeder Abschnitt kann einzeln erkundet werden.',
            filter: 'Verwende den Benutzerfilter oben, um alle anwendbaren Metriken auf einen einzelnen Benutzer einzugrenzen. Ein Filter-Symbol erscheint auf Karten, die von der Auswahl betroffen sind.',
            range: 'Passe den Datumsbereich oben an, um sich auf einen bestimmten Zeitraum zu konzentrieren. Die Standardansicht umfasst die letzten 90 Tage plus 30 Tage in die Zukunft.',
            tip: 'Tipp: Karten ohne Filter-Symbol zeigen immer globale Daten, unabhängig vom ausgewählten Benutzer.',
        },
        adminLogs: {
            what: 'Systemprotokolle zeigen die unformatierte Anwendungsprotokollausgabe. Nutze dies, um Fehler zu diagnostizieren, administrative Aktionen nachzuverfolgen und das Systemverhalten zu prüfen.',
            search: 'Verwende die Suchleiste, um Protokolleinträge nach Schlüsselwort zu filtern. Die Anzahl der übereinstimmenden und gesamten Zeilen wird angezeigt.',
            tip: 'Tipp: Protokolleinträge sind farblich nach Schweregrad kodiert: Info (Standard), Warnung (gelb) und Fehler (rot).',
        },
    },
    adminLogsPage: {
        title: 'System-Logs',
        subtitle: 'Anwendungsprotokolle und Debugging-Informationen anzeigen',
        searchPlaceholder: 'Logs durchsuchen ...',
        linesCount: '{count} Zeilen',
        filteredFrom: 'Gefiltert von {total}',
        noLogsAvailable: 'Keine Logs verfügbar.',
        noMatchingLogs: 'Keine Logs entsprechen Ihrer Suchanfrage.',
        failedToLoad:
            'Logs konnten nicht geladen werden. Möglicherweise hast du keine Berechtigung, diese Seite anzuzeigen.',
    },
    adminScreenshotsPage: {
        navButton: 'Screenshots',
        title: 'Debug-Screenshots',
        subtitle: 'Von Benutzern mit aktiviertem Debug-Modus hochgeladene Screenshots',
        empty: 'Es wurden noch keine Screenshots hochgeladen.',
        failedToLoad:
            'Screenshots konnten nicht geladen werden. Möglicherweise hast du keine Berechtigung, diese Seite anzuzeigen.',
        unknownUser: 'Unbekannter Benutzer',
        delete: 'Screenshot löschen',
        deleteConfirm: 'Diesen Screenshot löschen? Dies kann nicht rückgängig gemacht werden.',
        zoomIn: 'Vergrößern',
        zoomOut: 'Verkleinern',
        resetZoom: 'Zoom zurücksetzen',
        openOriginal: 'Original in neuem Tab öffnen',
    },
    debugScreenshot: {
        tooltip: 'Screenshot dieser Seite aufnehmen',
        success: 'Screenshot hochgeladen',
        error: 'Screenshot konnte nicht aufgenommen werden',
    },
    yearSummary: {
        label: 'Jahresübersicht',
        selectYearLabel: 'Jahr auswählen',
        date: 'Datum',
        time: 'Zeit',
        weekday: 'Wochentag',
        location: 'Ort',
        title: 'Titel',
        copied: 'Kopiert',
        generatedTitle: 'Generiertes Ereignis',
        copyButton: 'Kopieren',
        closeButton: 'Schließen',
    },
    personalForecast: {
        title: 'Persönliche Vorschau',
        subtitle: 'Deine bevorstehenden Termine und Kategorie-Zuweisungen',
        viewMode: 'Ansichtsmodus',
        byEntries: 'Nach Anzahl Termine',
        byMonths: 'Nach Anzahl Monate',
        numberOfEntries: 'Anzahl Termine',
        numberOfMonths: 'Anzahl Monate',
        noEvents: 'Keine bevorstehenden Termine mit deinen zugewiesenen Kategorien.',
        today: 'Heute',
        tomorrow: 'Morgen',
        inDays: 'In {count} Tag{{en}}',
        canceled: 'Abgesagt',
        noEventName: 'Unbenannte Veranstaltung',
        yourAssignments: 'Deine Kategorien:',
        you: 'Du',
        changeRequested: 'Änderung angefragt',
        requestedBy: 'Angefragt von {name}',
    },
} satisfies Translation;

export default de;
